/**
 * Content offline readiness — share of playlist items that can play with no network.
 *
 * Distinct from connection health (heartbeat) and sync status (desired vs reported
 * version). Those answer “is the screen reachable / on the right revision?”; this
 * answers “will that revision keep playing if the WAN drops?”
 *
 * Today GhostScreens publishes only uploaded image/video assets (cacheable by
 * construction). Classification stays explicit so future online-dependent kinds
 * (website, stream, live widget) can set `requiresConnectivity` without rewriting
 * dashboard math.
 */
function defaultBlockerLabel(item) {
    if (item.label?.trim())
        return item.label.trim();
    if (item.kind && item.kind !== 'image' && item.kind !== 'video') {
        return `${item.kind} item`;
    }
    if (item.mimeType?.startsWith('text/html'))
        return 'Web page';
    return 'Online-dependent item';
}
/**
 * Classifies one playlist item. Fail closed: unknown shapes are online-dependent
 * until the publish pipeline proves they are durable cache assets.
 */
export function classifyOfflineCapability(item) {
    if (item.requiresConnectivity === true)
        return 'online_dependent';
    if (item.requiresConnectivity === false)
        return 'offline_ready';
    if (item.kind === 'image' || item.kind === 'video')
        return 'offline_ready';
    const mime = item.mimeType ?? '';
    if (mime.startsWith('image/') || mime.startsWith('video/'))
        return 'offline_ready';
    return 'online_dependent';
}
/**
 * Derives offline readiness for a playlist version (or any ordered item list).
 * Returns `null` when there are no items — nothing to measure yet.
 */
export function deriveOfflineReadiness(items) {
    if (items.length === 0)
        return null;
    const blockers = [];
    let offlineReadyCount = 0;
    for (const item of items) {
        if (classifyOfflineCapability(item) === 'offline_ready') {
            offlineReadyCount += 1;
            continue;
        }
        blockers.push({ id: item.id, label: defaultBlockerLabel(item) });
    }
    const totalCount = items.length;
    const onlineDependentCount = blockers.length;
    const percent = Math.round((offlineReadyCount / totalCount) * 100);
    return {
        percent,
        totalCount,
        offlineReadyCount,
        onlineDependentCount,
        blockers,
    };
}
//# sourceMappingURL=offline-readiness.js.map